// Sala de video PROPIA (LiveKit self-hosted) — reemplazo de Google Meet (fase 1).
// Compartida por el médico (Teleconsulta) y el paciente (Portal):
//   1. PRE-TEST: prueba de cámara/micrófono (getUserMedia + medidor de nivel).
//   2. Paciente: check-in automático → SALA DE ESPERA (polling de med_tele_sala
//      cada 4 s hasta que el médico lo admita y llegue el token).
//   3. Video: LiveKitRoom con reconexión automática; controles en español
//      (micrófono, cámara, compartir pantalla, solo audio para redes lentas).
// Auditoría → med_tele_evento (join/leave/reconnect/calidad) — insumo del KPI
// del numeral 7.3.4 de la Norma Técnica de Telesalud (A.M. 00044-2025).
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LiveKitRoom, GridLayout, ParticipantTile, RoomAudioRenderer,
  useLocalParticipant, useRoomContext, useTracks,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { ConnectionQuality, RoomEvent, Track } from 'livekit-client';
import type { Participant } from 'livekit-client';
import { AlertTriangle, Loader2, Mic, MicOff, MonitorUp, PhoneOff, Video as VideoIcon, VideoOff, Volume2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { AutoTextarea, Modal, medRpc } from './shared';
import { EncuestaTele } from './TelePago';

export interface SalaInfo {
  id: string; estado: string; sala: string; url: string; token: string | null;
  consentimiento_aceptado: boolean; checkin_at: string | null; admitido_at: string | null;
  iniciada_at: string | null; acceso_expira: string | null; grabacion_consentida: boolean;
  paciente: string; medico: string;
}

const registrarEvento = (id: string, tipo: string, calidad?: string) => {
  void medRpc('med_tele_evento', { _id: id, _tipo: tipo, _calidad: calidad ?? null }).catch(() => undefined);
};

export function VideoSala({ teleconsultaId, actor, onCerrar, onConectado, panelEspera }: {
  teleconsultaId: string;
  actor: 'medico' | 'paciente';
  onCerrar: () => void;
  onConectado?: () => void;          // el médico lo usa para auto-iniciar la sesión
  panelEspera?: React.ReactNode;     // p. ej. el chat, visible mientras espera
}) {
  const qc = useQueryClient();
  const [etapa, setEtapa] = useState<'pretest' | 'video'>('pretest');
  const [soloAudio, setSoloAudio] = useState(false);
  const [encuesta, setEncuesta] = useState(false);
  const estuvoConectado = useRef(false);

  const { data: info } = useQuery<SalaInfo>({
    queryKey: ['med-tele-sala', teleconsultaId],
    queryFn: () => medRpc<SalaInfo>('med_tele_sala', { _id: teleconsultaId }),
    refetchInterval: (q) => (q.state.data?.token ? false : 4000),
  });

  // Check-in del paciente al abrir (idempotente; requiere consentimiento aceptado)
  const checkin = useMutation({
    mutationFn: () => medRpc('med_tele_checkin', { _id: teleconsultaId }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['med-tele-sala', teleconsultaId] }),
    onError: (e) => { toast.error(getErrorMessage(e)); onCerrar(); },
  });
  useEffect(() => {
    if (actor === 'paciente') checkin.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contraparte = info ? (actor === 'medico' ? info.paciente : info.medico) : '';

  return (
    <div className="flex h-[calc(100vh-10rem)] min-h-[520px] flex-col rounded-lg border bg-background">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div className="text-sm">
          <span className="font-semibold">Teleconsulta</span>
          {contraparte && <span className="text-muted-foreground"> · con {contraparte}</span>}
        </div>
        <button type="button" title="Cerrar" aria-label="Cerrar" className="rounded p-1 hover:bg-muted" onClick={onCerrar}>
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 min-h-0 flex flex-col">
        {etapa === 'pretest' ? (
          <PreTest soloAudio={soloAudio} setSoloAudio={setSoloAudio}
            onListo={() => setEtapa('video')} onCancelar={onCerrar} />
        ) : !info?.token ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6 text-center">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            {actor === 'paciente' ? (
              <>
                <p className="font-medium">Estás en la sala de espera</p>
                <p className="text-sm text-muted-foreground max-w-md">
                  El médico fue avisado de tu llegada y te admitirá en un momento. No cierres esta pantalla.
                </p>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Preparando la sala…</p>
            )}
            {panelEspera && <div className="w-full max-w-md border rounded-md flex flex-col h-72 text-left">{panelEspera}</div>}
            <Button variant="outline" size="sm" onClick={onCerrar}>Salir de la sala de espera</Button>
          </div>
        ) : (
          <LiveKitRoom
            serverUrl={info.url} token={info.token} connect
            audio video={!soloAudio}
            data-lk-theme="default" className="flex-1 min-h-0 flex flex-col"
            onConnected={() => { estuvoConectado.current = true; registrarEvento(teleconsultaId, 'join'); onConectado?.(); }}
            onDisconnected={() => {
              registrarEvento(teleconsultaId, 'leave');
              if (actor === 'paciente' && estuvoConectado.current) setEncuesta(true); else onCerrar();
            }}
            onError={(e) => toast.error(`Error de conexión: ${e.message}`)}
          >
            <RoomAudioRenderer />
            <MonitorConexion teleconsultaId={teleconsultaId} />
            <Escena />
            <Controles soloAudio={soloAudio} setSoloAudio={setSoloAudio} teleconsultaId={teleconsultaId} />
          </LiveKitRoom>
        )}
      </div>

      {encuesta && <EncuestaTele teleconsultaId={teleconsultaId} onListo={() => { setEncuesta(false); onCerrar(); }} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pre-test de dispositivos (cámara + medidor de micrófono) antes de entrar
// ---------------------------------------------------------------------------
function PreTest({ soloAudio, setSoloAudio, onListo, onCancelar }: {
  soloAudio: boolean; setSoloAudio: (v: boolean) => void; onListo: () => void; onCancelar: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [nivel, setNivel] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    let stream: MediaStream | undefined; let raf = 0; let ctx: AudioContext | undefined; let vivo = true;
    navigator.mediaDevices.getUserMedia({ video: true, audio: true })
      .then((s) => {
        if (!vivo) { s.getTracks().forEach((t) => t.stop()); return; }
        stream = s;
        if (videoRef.current) videoRef.current.srcObject = s;
        ctx = new AudioContext();
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 256;
        ctx.createMediaStreamSource(s).connect(analyser);
        const buf = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteFrequencyData(buf);
          setNivel(Math.min(100, Math.round((buf.reduce((a, b) => a + b, 0) / buf.length) * 1.6)));
          raf = requestAnimationFrame(tick);
        };
        tick();
      })
      .catch(() => setError('No se pudo acceder a la cámara o el micrófono. Revisa los permisos del navegador; puedes continuar y activarlos dentro de la sala.'));
    return () => { vivo = false; cancelAnimationFrame(raf); stream?.getTracks().forEach((t) => t.stop()); void ctx?.close().catch(() => undefined); };
  }, []);

  return (
    <div className="flex-1 flex flex-col items-center justify-center gap-4 p-6">
      <p className="font-medium">Prueba tu cámara y micrófono</p>
      <video ref={videoRef} autoPlay muted playsInline className="w-full max-w-md aspect-video rounded-lg border bg-black object-cover -scale-x-100" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-1"><Volume2 className="w-3.5 h-3.5" /> Nivel del micrófono (habla para probar)</div>
        <div className="h-2 rounded bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${nivel}%` }} /></div>
      </div>
      {error && <p className="max-w-md text-center text-xs text-amber-600">{error}</p>}
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={soloAudio} onChange={(e) => setSoloAudio(e.target.checked)} />
        Entrar solo con audio (recomendado si tu conexión es lenta)
      </label>
      <div className="flex gap-2">
        <Button variant="outline" onClick={onCancelar}>Cancelar</Button>
        <Button onClick={onListo}>Entrar a la consulta</Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Auditoría de conexión: reconexiones y cambios de calidad → med_tele_evento
// ---------------------------------------------------------------------------
function MonitorConexion({ teleconsultaId }: { teleconsultaId: string }) {
  const room = useRoomContext();
  const ultima = useRef<string>('');
  useEffect(() => {
    const alReconectar = () => registrarEvento(teleconsultaId, 'reconnect');
    const alCambiarCalidad = (q: ConnectionQuality, p: Participant) => {
      if (!p.isLocal) return;
      const mapa = q === ConnectionQuality.Excellent || q === ConnectionQuality.Good ? 'buena'
        : q === ConnectionQuality.Poor || q === ConnectionQuality.Lost ? 'mala' : 'regular';
      if (mapa !== ultima.current) { ultima.current = mapa; registrarEvento(teleconsultaId, 'calidad', mapa); }
    };
    room.on(RoomEvent.Reconnecting, alReconectar);
    room.on(RoomEvent.ConnectionQualityChanged, alCambiarCalidad);
    return () => {
      room.off(RoomEvent.Reconnecting, alReconectar);
      room.off(RoomEvent.ConnectionQualityChanged, alCambiarCalidad);
    };
  }, [room, teleconsultaId]);
  return null;
}

// ---------------------------------------------------------------------------
// Escena de video: tiles de cámara + pantalla compartida
// ---------------------------------------------------------------------------
function Escena() {
  const tracks = useTracks(
    [
      { source: Track.Source.Camera, withPlaceholder: true },
      { source: Track.Source.ScreenShare, withPlaceholder: false },
    ],
    { onlySubscribed: false },
  );
  return (
    <div className="flex-1 min-h-0">
      <GridLayout tracks={tracks} style={{ height: '100%' }}>
        <ParticipantTile />
      </GridLayout>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Barra de controles en español
// ---------------------------------------------------------------------------
function Controles({ soloAudio, setSoloAudio, teleconsultaId }: { soloAudio: boolean; setSoloAudio: (v: boolean) => void; teleconsultaId: string }) {
  const [adverso, setAdverso] = useState(false);
  const room = useRoomContext();
  const { localParticipant } = useLocalParticipant();
  const [mic, setMic] = useState(true);
  const [cam, setCam] = useState(!soloAudio);
  const [pantalla, setPantalla] = useState(false);

  const alternar = async (que: 'mic' | 'cam' | 'pantalla') => {
    try {
      if (que === 'mic') { await localParticipant.setMicrophoneEnabled(!mic); setMic(!mic); }
      if (que === 'cam') { await localParticipant.setCameraEnabled(!cam); setCam(!cam); if (cam) setSoloAudio(true); else setSoloAudio(false); }
      if (que === 'pantalla') { await localParticipant.setScreenShareEnabled(!pantalla); setPantalla(!pantalla); }
    } catch (e) { toast.error(getErrorMessage(e)); }
  };

  // text-foreground explícito: el tema de LiveKit (data-lk-theme) pinta los botones internos de blanco
  const boton = 'inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-sm text-foreground hover:bg-muted';
  return (
    <div className="flex flex-wrap items-center justify-center gap-2 border-t bg-card px-3 py-2.5">
      <button type="button" className={`${boton} ${mic ? '' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'}`} onClick={() => void alternar('mic')}>
        {mic ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />} {mic ? 'Silenciar' : 'Activar micrófono'}
      </button>
      <button type="button" className={`${boton} ${cam ? '' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`} onClick={() => void alternar('cam')}>
        {cam ? <VideoIcon className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />} {cam ? 'Apagar cámara' : 'Encender cámara'}
      </button>
      <button type="button" className={`${boton} ${pantalla ? 'bg-primary/10 text-primary' : ''}`} onClick={() => void alternar('pantalla')}>
        <MonitorUp className="w-4 h-4" /> {pantalla ? 'Dejar de compartir' : 'Compartir pantalla'}
      </button>
      <button type="button" className={`${boton} text-amber-600 dark:text-amber-400`} onClick={() => setAdverso(true)}>
        <AlertTriangle className="w-4 h-4" /> Evento adverso
      </button>
      <button type="button" className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-2 text-sm text-white hover:bg-red-700" onClick={() => void room.disconnect()}>
        <PhoneOff className="w-4 h-4" /> Salir
      </button>
      {adverso && <ModalEventoAdverso teleconsultaId={teleconsultaId} onClose={() => setAdverso(false)} />}
    </div>
  );
}

// Reporte de evento adverso durante la teleconsulta (A.M. 00044-2025, 7.3.3)
function ModalEventoAdverso({ teleconsultaId, onClose }: { teleconsultaId: string; onClose: () => void }) {
  const [descripcion, setDescripcion] = useState('');
  const [enviando, setEnviando] = useState(false);
  const enviar = async () => {
    setEnviando(true);
    try {
      await medRpc('med_tele_evento', { _id: teleconsultaId, _tipo: 'evento_adverso', _calidad: null, _detalle: { descripcion } });
      toast.success('Evento adverso reportado');
      onClose();
    } catch (e) { toast.error(getErrorMessage(e)); } finally { setEnviando(false); }
  };
  return (
    <Modal title="Reportar evento adverso" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">Describe el evento adverso ocurrido durante la teleconsulta (queda registrado para la dirección médica).</p>
        <AutoTextarea autoFocus value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button disabled={!descripcion.trim()} isLoading={enviando} onClick={() => void enviar()}>Reportar</Button>
        </div>
      </div>
    </Modal>
  );
}
